import { getCookie } from "@/app/utils/client-cookies"

export type AdsConsentStatus = "granted" | "refused"

// Google Consent Mode d'Axeptio : « Données utilisateur pour la pub », le seul signal dont le nom ne dépend pas du back-office
const GOOGLE_CONSENT_MODE_KEY = "$$googleConsentMode"
const AD_USER_DATA_KEY = "ad_user_data"

/**
 * Repli quand le bandeau ne publie pas Google Consent Mode : vendeurs portant la pub, accepter l'un d'eux vaut accord.
 * Les slugs sont définis dans le back-office Axeptio et y ont déjà été renommés (d'où les doublons de casse) — vérifier
 * le cookie réel avant d'y toucher.
 */
export const ADS_VENDOR_KEYS = ["google_ads", "Google_Ads", "meta_pixel", "microsoft", "tiktok", "TikTok"]

// détail des choix persisté par Axeptio : les vendeurs refusés y sont à false, contrairement à la liste des autorisés
const AXEPTIO_CHOICES_COOKIE = "axeptio_cookies"

type AxeptioChoices = Record<string, unknown>
type AdsConsentListener = (status: AdsConsentStatus) => void

const listeners = new Set<AdsConsentListener>()
let current: AdsConsentStatus | null = null
let subscribed = false

/** null = jamais collecté : le bandeau n'a pas répondu. Un refus explicite vaut "refused", jamais null. */
export function resolveAdsConsent(choices: AxeptioChoices | null | undefined): AdsConsentStatus | null {
    if (!choices || typeof choices !== "object") return null

    const declared = readAdUserData(choices)
    if (declared !== null) return declared

    const answers = ADS_VENDOR_KEYS.filter((key) => typeof choices[key] === "boolean")
    if (answers.length === 0) return null

    return answers.some((key) => choices[key] === true) ? "granted" : "refused"
}

// « Continuer sans accepter » renseigne ad_user_data à denied : le lire évite de déduire un refus d'une liste de vendeurs
function readAdUserData(choices: AxeptioChoices): AdsConsentStatus | null {
    const consentMode = choices[GOOGLE_CONSENT_MODE_KEY]
    if (!consentMode || typeof consentMode !== "object") return null

    const value = (consentMode as Record<string, unknown>)[AD_USER_DATA_KEY]
    if (value === "granted") return "granted"
    if (value === "denied") return "refused"

    return null
}

/** Choix d'une visite précédente : le bandeau ne se rouvre pas, donc aucun évènement ne le rejouera. */
export function readStoredAdsConsent(): AdsConsentStatus | null {
    const raw = getCookie(AXEPTIO_CHOICES_COOKIE)
    if (!raw) return null

    try {
        return resolveAdsConsent(JSON.parse(decodeURIComponent(raw)))
    } catch {
        return null
    }
}

export function getAdsConsent(): AdsConsentStatus | null {
    return current
}

/** S'abonne au choix du bandeau ; le callback reçoit aussi le choix déjà connu. Renvoie le désabonnement. */
export function onAdsConsentChange(listener: AdsConsentListener): () => void {
    listeners.add(listener)
    subscribeToAxeptio()

    if (current !== null) listener(current)

    return () => {
        listeners.delete(listener)
    }
}

function publish(status: AdsConsentStatus | null): void {
    if (status === null || status === current) return

    current = status
    listeners.forEach((listener) => listener(status))
}

function subscribeToAxeptio(): void {
    if (subscribed || typeof window === "undefined") return
    subscribed = true

    // le cookie d'abord : s'abonner ne rejoue pas forcément un choix déjà exprimé bien avant la naissance du lead
    publish(readStoredAdsConsent())

    // _axcb met la callback en file d'attente jusqu'à l'arrivée du SDK, injecté par GTM — jamais servie sans bandeau
    window._axcb = window._axcb || []
    window._axcb.push((sdk) => {
        sdk.on("cookies:complete", (choices: AxeptioChoices) => publish(resolveAdsConsent(choices)))
    })
}
